import { Card, CardBody } from 'reactstrap';
import { Link } from 'react-router-dom';

export type KpiTone = 'neutral' | 'success' | 'warn' | 'danger' | 'info' | 'primary';

export interface KpiProps {
  label: string;
  value: string | number;
  delta?: string;
  tone?: KpiTone;
  hint?: string;
  /**
   * dashboard-kpi: icona avatar Velzon (es. 'bi-clipboard-check').
   * Quando passata, viene mostrata in un cerchio colorato in base a `iconTone`.
   */
  icon?: string;
  /**
   * Sfumatura per l'avatar dell'icona (background subtle + testo tinto).
   * Default = `tone` se non passato.
   */
  iconTone?: KpiTone;
  /**
   * dashboard-kpi: se valorizzato, l'intera card diventa un <Link>
   * clickabile che naviga all'URL. Hover lift viene dal card-animate
   * Velzon. Esempio: "/pick-lists?status=fulfilled".
   */
  href?: string;
}

const TONE_TEXT: Record<KpiTone, string> = {
  neutral: 'text-muted',
  success: 'text-success',
  warn: 'text-warning',
  danger: 'text-danger',
  info: 'text-info',
  primary: 'text-primary',
};

const ICON_BG: Record<KpiTone, string> = {
  neutral: 'bg-secondary-subtle text-secondary',
  success: 'bg-success-subtle text-success',
  warn: 'bg-warning-subtle text-warning',
  danger: 'bg-danger-subtle text-danger',
  info: 'bg-info-subtle text-info',
  primary: 'bg-primary-subtle text-primary',
};

/**
 * KPI widget Velzon. Layout adattivo:
 *  - senza `icon`: rendering minimalista (label / value / delta / hint).
 *  - con `icon`: avatar Velzon a sinistra + value/label a destra (pattern
 *    "counter-widget" tipico delle dashboard Velzon).
 *  - con `href`: l'intera CardBody diventa cliccabile via <Link>.
 */
export function Kpi({ label, value, delta, tone = 'neutral', hint, icon, iconTone, href }: KpiProps) {
  const inner = icon ? (
    <CardBody>
      <div className="d-flex align-items-center">
        <div className="avatar-sm flex-shrink-0 me-3">
          <span className={`avatar-title rounded-circle fs-20 ${ICON_BG[iconTone ?? tone]}`}>
            <i className={`bi ${icon}`} />
          </span>
        </div>
        <div className="flex-grow-1 overflow-hidden">
          <p className="vp-label text-truncate mb-1">{label}</p>
          <div className="d-flex align-items-baseline gap-2">
            <h4 className="fs-22 fw-semibold mb-0">{value}</h4>
            {delta && <span className={`fw-medium fs-13 ${TONE_TEXT[tone]}`}>{delta}</span>}
          </div>
          {hint && <p className="text-muted small mb-0 mt-1">{hint}</p>}
        </div>
      </div>
    </CardBody>
  ) : (
    <CardBody>
      <p className="vp-label text-truncate mb-2">{label}</p>
      <h4 className="fs-22 fw-semibold mb-0">{value}</h4>
      {delta && <span className={`fw-medium fs-13 ${TONE_TEXT[tone]}`}>{delta}</span>}
      {hint && <p className="text-muted small mb-0 mt-2">{hint}</p>}
    </CardBody>
  );

  if (href !== undefined) {
    return (
      <Card className="card-animate mb-0 h-100">
        <Link to={href} className="text-reset text-decoration-none d-block h-100">
          {inner}
        </Link>
      </Card>
    );
  }

  return <Card className="card-animate mb-0 h-100">{inner}</Card>;
}
